import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ApiError, apiClient } from "@/lib/api-client";

type WalletBalance = {
  wallets: {
    gencap: { balance: number };
    topup: { balance: number };
  };
  totalCredits: number;
};

type Price = {
  id: string;
  unit_amount: number;
  recurring?: {
    interval: string;
  };
  product: {
    id: string;
    name: string;
    description?: string | null;
  };
};

type PricesResponse = {
  prices: Price[];
};

export const Route = createFileRoute("/$locale/_dashboard/billings")({
  component: BillingsPage,
});

function getCheckoutErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.message) as { error?: string };
      if (parsed?.error === "TOPUP_DEBT_BLOCKED") {
        return "Top-up credits are temporarily blocked until refund reconciliation is completed.";
      }
      if (parsed?.error) {
        return parsed.error;
      }
    } catch {
      // Fallback to raw message below.
    }

    if (err.message.includes("TOPUP_DEBT_BLOCKED")) {
      return "Top-up credits are temporarily blocked until refund reconciliation is completed.";
    }

    return err.message;
  }

  if (err instanceof Error) {
    return err.message;
  }

  return "Failed to start checkout. Please try again.";
}

function BillingsPage() {
  const [balance, setBalance] = useState<WalletBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [topupPrices, setTopupPrices] = useState<Price[]>([]);
  const [topupLoading, setTopupLoading] = useState(true);
  const [topupCheckoutId, setTopupCheckoutId] = useState<string | null>(null);

  useEffect(() => {
    async function fetchBalance() {
      try {
        const data = await apiClient<WalletBalance>("/api/wallet/balance");
        setBalance(data);
      } catch (err) {
        console.error("Failed to fetch balance:", err);
      } finally {
        setLoading(false);
      }
    }

    async function fetchTopupPrices() {
      try {
        const data = await apiClient<PricesResponse>("/api/stripe/prices");
        setTopupPrices(
          data.prices
            .filter((price) => !price.recurring)
            .sort((a, b) => a.unit_amount - b.unit_amount),
        );
      } catch (err) {
        console.error("Failed to fetch top-up prices:", err);
      } finally {
        setTopupLoading(false);
      }
    }

    fetchBalance();
    fetchTopupPrices();
  }, []);

  async function openBillingPortal() {
    setPortalLoading(true);
    try {
      const data = await apiClient<{ url: string }>(
        "/api/stripe/billing-portal/sessions",
        { method: "POST" },
      );
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Portal error:", err);
      alert("Failed to open billing portal. You may not have an active subscription.");
    } finally {
      setPortalLoading(false);
    }
  }

  async function startTopupCheckout(priceId: string) {
    setTopupCheckoutId(priceId);
    try {
      const data = await apiClient<{ url: string }>(
        "/api/stripe/checkout/sessions",
        {
          method: "POST",
          body: JSON.stringify({ priceId, kind: "topup" }),
        },
      );
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Top-up checkout error:", err);
      alert(getCheckoutErrorMessage(err));
    } finally {
      setTopupCheckoutId(null);
    }
  }

  return (
    <div className="py-6">
      <h1 className="text-2xl font-semibold">Billings</h1>
      <p className="text-muted-foreground mt-2">
        Manage your billing and subscription settings.
      </p>

      <div className="mt-8 grid md:grid-cols-2 gap-6 max-w-3xl">
        <div className="border rounded-lg p-6">
          <h2 className="text-lg font-medium mb-4">Credit Balance</h2>
          {loading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : balance ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subscription Credits</span>
                <span className="font-medium">{balance.wallets.gencap.balance}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Top-up Credits</span>
                <span className="font-medium">{balance.wallets.topup.balance}</span>
              </div>
              <div className="border-t pt-3 flex justify-between">
                <span className="font-medium">Total</span>
                <span className="font-semibold text-lg">{balance.totalCredits}</span>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">Unable to load balance</p>
          )}
        </div>

        <div className="border rounded-lg p-6">
          <h2 className="text-lg font-medium mb-4">Subscription</h2>
          <button
            type="button"
            onClick={openBillingPortal}
            disabled={portalLoading}
            className="w-full bg-secondary text-secondary-foreground rounded-md py-2 disabled:opacity-50"
          >
            {portalLoading ? "Opening..." : "Manage Subscription"}
          </button>
        </div>
      </div>

      <div className="mt-8 max-w-3xl border rounded-lg p-6">
        <h2 className="text-lg font-medium">Buy Top-up Credits</h2>
        <p className="text-muted-foreground mt-2">
          One-time credit packs for additional usage.
        </p>

        {topupLoading ? (
          <p className="text-muted-foreground mt-4">Loading top-up packs...</p>
        ) : topupPrices.length === 0 ? (
          <p className="text-muted-foreground mt-4">No top-up packs available.</p>
        ) : (
          <div className="mt-5 grid md:grid-cols-2 gap-4">
            {topupPrices.map((price) => (
              <div key={price.id} className="border rounded-md p-4 flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium">{price.product.name}</p>
                    {price.product.description ? (
                      <p className="text-sm text-muted-foreground mt-1">
                        {price.product.description}
                      </p>
                    ) : null}
                  </div>
                  <p className="font-semibold">${(price.unit_amount ?? 0) / 100}</p>
                </div>

                <button
                  type="button"
                  onClick={() => startTopupCheckout(price.id)}
                  disabled={topupCheckoutId === price.id}
                  className="mt-4 bg-primary text-primary-foreground rounded-md py-2 disabled:opacity-50"
                >
                  {topupCheckoutId === price.id ? "Processing..." : "Buy Credits"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
